import { useState, useCallback, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';
import { STONE_SHAPES, SETTING_TYPES } from '@/utils/constants';

const FIELDS = ['category', 'stone_category', 'stone_shape', 'stone_quality', 'setting_type', 'size_mm', 'weight_per_stone', 'quantity'];
const HEADERS = ['Category', 'Type', 'Shape', 'Quality', 'Setting', 'Size (mm)', 'Wt/pc (ct)', 'Qty', 'Total Wt'];

const emptyRow = () => ({
  key: crypto.randomUUID(),
  category: 'Accent Stone', stone_category: 'Natural', stone_shape: 'Round', stone_quality: '', setting_type: 'Prong',
  size_mm: '', weight_per_stone: '', quantity: '',
});

const stoneToRow = (s) => ({
  key: crypto.randomUUID(),
  category: s.category || 'Accent Stone',
  stone_category: s.stone_category || 'Natural',
  stone_shape: s.stone_shape || 'Round',
  stone_quality: s.stone_quality || '',
  setting_type: s.setting_type || 'Prong',
  size_mm: s.size_mm || '',
  weight_per_stone: s.weight_per_stone || '',
  quantity: s.quantity || '',
});

export function TaskStoneImportDialog({ open, onOpenChange, onImport, existingStones = [] }) {
  const [rows, setRows] = useState([]);
  const [activeCell, setActiveCell] = useState(null);
  const gridRef = useRef(null);

  useEffect(() => {
    if (open) {
      const existing = existingStones.map(stoneToRow);
      const empties = Array.from({ length: Math.max(3, 8 - existing.length) }, emptyRow);
      setRows([...existing, ...empties]);
    }
  }, [open, existingStones]);

  const updateCell = (rowIdx, field, value) => {
    setRows(prev => prev.map((r, i) => i === rowIdx ? { ...r, [field]: value } : r));
  };

  const removeRow = (idx) => {
    setRows(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
  };

  const addRows = (count = 3) => {
    setRows(prev => [...prev, ...Array.from({ length: count }, emptyRow)]);
  };

  const handlePaste = useCallback((e) => {
    const text = e.clipboardData.getData('text');
    const lines = text.split('\n').filter(l => l.trim());
    if (lines.length <= 1 && !lines[0]?.includes('\t')) return;
    e.preventDefault();
    const startRow = activeCell?.row ?? 0;
    const startCol = activeCell?.col ?? 0;
    setRows(prev => {
      const newRows = [...prev];
      lines.forEach((line, li) => {
        const cols = line.split('\t');
        const ri = startRow + li;
        while (newRows.length <= ri) newRows.push(emptyRow());
        cols.forEach((val, ci) => {
          const fi = startCol + ci;
          if (fi < FIELDS.length) newRows[ri][FIELDS[fi]] = val.trim();
        });
      });
      return newRows;
    });
  }, [activeCell]);

  const handleKeyDown = useCallback((e, rowIdx, colIdx) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const nextCol = e.shiftKey ? colIdx - 1 : colIdx + 1;
      if (nextCol >= 0 && nextCol < FIELDS.length) {
        setActiveCell({ row: rowIdx, col: nextCol });
        const cell = gridRef.current?.querySelector(`[data-row="${rowIdx}"][data-col="${nextCol}"]`);
        cell?.focus();
      } else if (!e.shiftKey && nextCol >= FIELDS.length && rowIdx + 1 < rows.length) {
        setActiveCell({ row: rowIdx + 1, col: 0 });
        const cell = gridRef.current?.querySelector(`[data-row="${rowIdx + 1}"][data-col="0"]`);
        cell?.focus();
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (rowIdx + 1 < rows.length) {
        setActiveCell({ row: rowIdx + 1, col: colIdx });
        const cell = gridRef.current?.querySelector(`[data-row="${rowIdx + 1}"][data-col="${colIdx}"]`);
        cell?.focus();
      }
    }
  }, [rows.length]);

  const handleImport = () => {
    const valid = rows.filter(r => r.stone_shape || r.stone_quality || r.weight_per_stone || r.quantity);
    if (valid.length === 0) return;
    const mapped = valid.map(r => ({
      id: crypto.randomUUID(),
      category: r.category || 'Accent Stone',
      stone_category: r.stone_category || 'Natural',
      stone_shape: r.stone_shape || 'Round',
      stone_quality: r.stone_quality || '',
      setting_type: r.setting_type || 'Prong',
      size_mm: r.size_mm || '',
      weight_per_stone: r.weight_per_stone || '',
      quantity: parseInt(r.quantity) || 1,
      total_weight: (parseFloat(r.weight_per_stone) || 0) * (parseInt(r.quantity) || 1),
    }));
    onImport(mapped);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-5 pt-5 pb-2">
          <DialogTitle>Bulk Add / Edit Stones</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Edit, add rows, or paste from Excel/Google Sheets (Tab-separated).
          </DialogDescription>
        </DialogHeader>
        <div ref={gridRef} className="flex-1 overflow-auto px-5" onPaste={handlePaste}>
          <div className="border border-border rounded-md overflow-hidden">
            <div className="grid grid-cols-[90px_100px_100px_80px_90px_70px_80px_60px_70px_32px] bg-muted/60 border-b border-border">
              {HEADERS.map(h => (
                <div key={h} className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-r border-border last:border-r-0">{h}</div>
              ))}
              <div />
            </div>
            {rows.map((row, ri) => (
              <div key={row.key} className="grid grid-cols-[90px_100px_100px_80px_90px_70px_80px_60px_70px_32px] border-b border-border/50 last:border-b-0 hover:bg-muted/20">
                {FIELDS.map((field, ci) => (
                  <div key={field} className="border-r border-border/50">
                    <input
                      data-row={ri}
                      data-col={ci}
                      className="w-full h-8 px-2 text-xs bg-transparent outline-none focus:ring-1 focus:ring-inset focus:ring-ring"
                      value={row[field]}
                      onChange={e => updateCell(ri, field, e.target.value)}
                      onFocus={() => setActiveCell({ row: ri, col: ci })}
                      onKeyDown={e => handleKeyDown(e, ri, ci)}
                      list={field === 'stone_shape' ? 'shape-list' : field === 'setting_type' ? 'setting-list' : undefined}
                      placeholder={['Accent Stone', 'Natural', 'Round', 'VS1', 'Prong', '2.0', '0.10', '4'][ci] || ''}
                    />
                  </div>
                ))}
                <div className="border-r border-border/50 flex items-center px-2 text-xs text-muted-foreground">
                  {((parseFloat(row.weight_per_stone) || 0) * (parseInt(row.quantity) || 0)).toFixed(3)}
                </div>
                <div className="flex items-center justify-center">
                  <button className="p-1 text-muted-foreground hover:text-destructive transition-colors" onClick={() => removeRow(ri)} tabIndex={-1}>
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <datalist id="shape-list">{STONE_SHAPES.map(s => <option key={s} value={s} />)}</datalist>
          <datalist id="setting-list">{SETTING_TYPES.map(s => <option key={s} value={s} />)}</datalist>
          <Button variant="ghost" size="sm" onClick={() => addRows()} className="mt-2 text-xs h-7">
            <Plus className="h-3 w-3 mr-1" /> Add Rows
          </Button>
        </div>
        <DialogFooter className="px-5 pb-5 pt-3 border-t border-border">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={handleImport} data-testid="task-import-stones-btn">Save All Stones</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
